/**
 * Field time-tracking hooks + helpers.
 *
 * The server's TimeEntry log is the source of truth. The PWA renders a
 * live clock by reading the open entry's `startedAt` and ticking from a
 * local 1Hz interval — server reconciliation happens on the next refetch
 * (TanStack Query's 30s staleTime + window-focus refresh).
 *
 * For the "total time on this job" KPI we walk all entries (closed + the
 * one open) and sum durationSec. Closed entries have a server-stamped
 * durationSec; the open one is computed client-side as
 * `(now - startedAt) / 1000`.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, type ActiveJob, type FieldPhase, type TimeEntry } from './api.js';

const ACTIVE_KEY = ['field', 'active'] as const;

/**
 * Reads /field/active. Stale-while-revalidate at 30s — same as the
 * dispatch board — and refetches on window focus so a tech who locks
 * their phone gets a fresh state when they come back.
 */
export function useActiveJob() {
  return useQuery({
    queryKey: ACTIVE_KEY,
    queryFn: ({ signal }) => api.field.active(signal),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export const ACTIVE_JOB_QUERY_KEY = ACTIVE_KEY;

/**
 * 1Hz live clock for the open entry. Reads the server's `startedAt`
 * and ticks locally. Returns 0 when no entry is open.
 */
export function useOpenEntrySeconds(active: ActiveJob | null | undefined): number {
  const startedAt = active?.openEntry.startedAt ?? null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  if (!startedAt) return 0;
  const start = Date.parse(startedAt);
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, Math.floor((now - start) / 1000));
}

/** Sum durationSec across all closed entries. The open one is computed via
 * useOpenEntrySeconds and added by the caller — we don't try to merge
 * the two because the open one ticks every second and we want the closed
 * total memoised. */
export function closedSecondsFromEntries(entries: ReadonlyArray<TimeEntry>): number {
  return entries.reduce((acc, e) => acc + (e.durationSec ?? 0), 0);
}

/** Format `seconds` as `H:MM:SS` (drops leading hour when 0). */
export function formatTimer(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${String(h)}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Human-readable label for a phase. */
export function phaseLabel(phase: FieldPhase): string {
  switch (phase) {
    case 'scheduled':
      return 'Scheduled';
    case 'traveling':
      return 'Traveling';
    case 'on_site':
      return 'On site';
    case 'work_in_progress':
      return 'Work in progress';
    case 'paused':
      return 'Paused';
    case 'work_completed':
      return 'Work completed';
    case 'signed_off':
      return 'Signed off';
  }
}

/** Pill variant for a phase — feeds into the existing Pill component. */
export function phasePillVariant(phase: FieldPhase): 'info' | 'success' | 'warning' | 'neutral' {
  switch (phase) {
    case 'traveling':
      return 'info';
    case 'on_site':
    case 'work_in_progress':
      return 'info';
    case 'paused':
      return 'warning';
    case 'work_completed':
    case 'signed_off':
      return 'success';
    case 'scheduled':
    default:
      return 'neutral';
  }
}

/** Linear next phase. Returns null for off-axis phases. */
export function nextLinearPhase(current: FieldPhase): FieldPhase | null {
  const order: FieldPhase[] = [
    'traveling',
    'on_site',
    'work_in_progress',
    'work_completed',
    'signed_off',
  ];
  const idx = order.indexOf(current);
  if (idx === -1) return null;
  return order[idx + 1] ?? null;
}
